import * as THREE from 'three';
import type { BridgePin } from '../../../embed/MapBridge';
import type { IMapSurface } from '../MapSurface';
import { categoryPinMarkerScale, categoryPinStackRank, isCategoryCubePin } from './MapCategoryPinNode';
import type { MapMarkerMetricsWorld } from './MapMarkerMetrics';

export interface MapStackPlacement {
    local: THREE.Vector3;
    poleHeight: number;
}

/** Complete-link category clustering: every member of a stack is within the radius. */
export function buildCategoryStackLayout(
    pins: BridgePin[],
    resolved: ReadonlyMap<number, THREE.Vector3>,
    surface: IMapSurface,
    metrics: MapMarkerMetricsWorld,
): Map<number, MapStackPlacement> {
    const candidates = pins
        .filter(pin => isCategoryCubePin(pin) && resolved.has(pin.id))
        .sort((a, b) => a.id - b.id);
    const clusters: BridgePin[][] = [];
    for (const pin of candidates) {
        const point = resolved.get(pin.id)!;
        const cluster = clusters.find(members => members.every(member => {
            const other = resolved.get(member.id)!;
            return Math.hypot(point.x - other.x, point.z - other.z) <= metrics.categoryClusterRadius;
        }));
        if (cluster) cluster.push(pin);
        else clusters.push([pin]);
    }

    const result = new Map<number, MapStackPlacement>();
    for (const members of clusters) {
        members.sort((a, b) => categoryPinStackRank(a) - categoryPinStackRank(b) || a.id - b.id);
        const center = averagePosition(members, resolved);
        const groundY = surface.getWorldY(center.x, center.z);
        if (groundY === null) continue;
        let nextBottom = groundY + metrics.categoryPoleHeight;
        members.forEach((pin, index) => {
            const markerSize = metrics.categoryCubeSize * categoryPinMarkerScale(pin);
            result.set(pin.id, {
                local: new THREE.Vector3(center.x, nextBottom + markerSize * 0.5, center.z),
                poleHeight: index === 0 ? metrics.categoryPoleHeight : 0,
            });
            nextBottom += markerSize * metrics.stackGapFactor;
        });
    }
    return result;
}

export function buildExplicitStackLayout(
    pins: BridgePin[],
    resolved: ReadonlyMap<number, THREE.Vector3>,
    surface: IMapSurface,
    options: {
        includes: (pin: BridgePin) => boolean;
        rank: (pin: BridgePin) => number;
        cubeSize: number;
        poleHeight: number;
        gapFactor?: number;
    },
): Map<number, MapStackPlacement> {
    const members = pins
        .filter(pin => options.includes(pin) && resolved.has(pin.id))
        .sort((a, b) => options.rank(a) - options.rank(b) || a.id - b.id);
    const result = new Map<number, MapStackPlacement>();
    if (members.length === 0) return result;
    const center = averagePosition(members, resolved);
    const groundY = surface.getWorldY(center.x, center.z);
    if (groundY === null) return result;
    let nextBottom = groundY + options.poleHeight;
    for (const [index, pin] of members.entries()) {
        result.set(pin.id, {
            local: new THREE.Vector3(center.x, nextBottom + options.cubeSize * 0.5, center.z),
            poleHeight: index === 0 ? options.poleHeight : 0,
        });
        nextBottom += options.cubeSize * (options.gapFactor ?? 1.12);
    }
    return result;
}

function averagePosition(
    members: BridgePin[],
    resolved: ReadonlyMap<number, THREE.Vector3>,
): THREE.Vector3 {
    const center = new THREE.Vector3();
    for (const member of members) center.add(resolved.get(member.id)!);
    return center.divideScalar(members.length);
}
